/**
 * ogreclan.com's Markdown (markdown-parse.ts), drawn: every Pokemon name with its sprite to its left, and every
 * Pokepaste link's team as a card of its Pokemon's sprites and the paste's title, which open the paste.
 */
import type { PokemonData } from '@ogreclan/pokemon-core';
import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { api } from './api';
import { parseBlocks, pokemonMatcher, pokemonNames, type Block, type Inline, type TextPiece } from './markdown-parse';
import { Sprite, usePokemon } from './pokemon';

/** Every Pokemon, Champions' new Megas too */
const DATA_MOD = 'championsnatdex';

const matchers = new WeakMap<PokemonData, (text: string) => TextPiece[]>();
function matcherFor(data: PokemonData) {
	let matcher = matchers.get(data);
	if (!matcher) {
		const species = data.allSpeciesIds().map(id => data.getSpecies(id)).filter(s => !!s).map(s => ({ id: s.id, name: s.name }));
		matcher = pokemonMatcher(pokemonNames(species), name => data.getSpecies(name)?.id ?? null);
		matchers.set(data, matcher);
	}
	return matcher;
}

interface Context { data: PokemonData | null, find: ((text: string) => TextPiece[]) | null }

function Text({ text, ctx }: { text: string, ctx: Context }) {
	if (!ctx.find || !ctx.data) return <>{text}</>;
	return <>{ctx.find(text).map((piece, i) => {
		const species = 'pokemon' in piece ? ctx.data!.getSpecies(piece.pokemon) : null;
		return species ? <span key={i} class="mon-inline"><Sprite data={ctx.data} species={species} />{piece.text}</span> : piece.text;
	})}</>;
}

function Inlines({ nodes, ctx, plain = false }: { nodes: Inline[], ctx: Context, plain?: boolean }): preact.JSX.Element {
	return <>{nodes.map((node, i) => {
		switch (node.type) {
		case 'text': return plain ? node.text : <Text key={i} text={node.text} ctx={ctx} />;
		case 'code': return <code key={i}>{node.text}</code>;
		case 'break': return <br key={i} />;
		case 'strong': return <strong key={i}><Inlines nodes={node.children} ctx={ctx} plain={plain} /></strong>;
		case 'em': return <em key={i}><Inlines nodes={node.children} ctx={ctx} plain={plain} /></em>;
		case 'del': return <del key={i}><Inlines nodes={node.children} ctx={ctx} plain={plain} /></del>;
		case 'image': return <img key={i} src={node.src} alt={node.alt} loading="lazy" class="md-image" />;
		case 'link': {
			const external = /^https?:/i.test(node.href);
			return <a key={i} href={node.href} {...external ? { target: '_blank', rel: 'noopener noreferrer' } : {}}>
				<Inlines nodes={node.children} ctx={ctx} plain={plain} />
			</a>;
		}
		}
	})}</>;
}

interface PasteInfo { id: string, url: string, title: string, author: string, species: string[] }

/** A Pokepaste's team: its Pokemon's sprites and its title, the whole card a link to the paste */
export function PasteEmbed({ id, data }: { id: string, data: PokemonData | null }) {
	const [paste, setPaste] = useState<PasteInfo | null>(null);
	const [failed, setFailed] = useState(false);
	useEffect(() => {
		let current = true;
		setPaste(null);
		setFailed(false);
		api.get<PasteInfo>(`/api/home/pastes/${id}`).then(found => current && setPaste(found), () => current && setFailed(true));
		return () => {
			current = false;
		};
	}, [id]);
	const url = `https://pokepast.es/${id}`;
	return <a class="paste-embed" href={url} target="_blank" rel="noopener noreferrer">
		<span class="paste-sprites">
			{paste ? paste.species.map((name, i) => {
				const species = data?.getSpecies(name);
				return species ? <span key={i} class="paste-mon" title={species.name}><Sprite data={data} species={species} /></span> :
					<span key={i} class="paste-mon unknown">{name}</span>;
			}) : <span class="muted small">{failed ? `Couldn't load this team` : 'Loading the team…'}</span>}
		</span>
		<span class="paste-caption">
			<strong>{paste?.title || 'Pokepaste'}</strong>
			{paste?.author && <span class="muted"> by {paste.author}</span>}
		</span>
	</a>;
}

function BlockView({ block, ctx }: { block: Block, ctx: Context }): preact.JSX.Element {
	switch (block.type) {
	case 'heading': {
		const Tag = `h${Math.min(6, block.level + 1)}` as 'h2';
		return <Tag><Inlines nodes={block.inline} ctx={ctx} /></Tag>;
	}
	case 'paragraph': return <p><Inlines nodes={block.inline} ctx={ctx} /></p>;
	case 'code': return <pre><code>{block.text}</code></pre>;
	case 'quote': return <blockquote><Blocks blocks={block.blocks} ctx={ctx} /></blockquote>;
	case 'rule': return <hr />;
	case 'paste': return <PasteEmbed id={block.id} data={ctx.data} />;
	case 'list': {
		const items = block.items.map((item, i) => <li key={i}><Blocks blocks={item} ctx={ctx} tight /></li>);
		return block.ordered ? <ol start={block.start}>{items}</ol> : <ul>{items}</ul>;
	}
	case 'table': return <div class="table-wrap"><table class="md-table">
		<thead><tr>{block.head.map((cell, i) => <th key={i} style={block.align[i] ? { textAlign: block.align[i] } : undefined}>
			<Inlines nodes={cell} ctx={ctx} /></th>)}</tr></thead>
		<tbody>{block.rows.map((row, r) => <tr key={r}>{row.map((cell, i) =>
			<td key={i} style={block.align[i] ? { textAlign: block.align[i] } : undefined}><Inlines nodes={cell} ctx={ctx} /></td>)}</tr>)}</tbody>
	</table></div>;
	}
}

function Blocks({ blocks, ctx, tight = false }: { blocks: Block[], ctx: Context, tight?: boolean }) {
	// a list item's single paragraph is just its text
	if (tight && blocks.length === 1 && blocks[0]!.type === 'paragraph') return <Inlines nodes={blocks[0]!.inline} ctx={ctx} />;
	return <>{blocks.map((block, i) => <BlockView key={i} block={block} ctx={ctx} />)}</>;
}

/** Some Markdown, drawn (sprites appear once the Pokemon data has loaded) */
export function Markdown({ text, class: className }: { text: string, class?: string }) {
	const data = usePokemon(DATA_MOD);
	const blocks = useMemo(() => parseBlocks(text), [text]);
	const ctx = useMemo(() => ({ data, find: data ? matcherFor(data) : null }), [data]);
	return <div class={`markdown${className ? ` ${className}` : ''}`}><Blocks blocks={blocks} ctx={ctx} /></div>;
}

/** A Markdown text box with a preview beside it (under it on a phone), and what Markdown can do */
export function MarkdownEditor({ value, onInput, rows = 16, label }: { value: string, onInput: (text: string) => void, rows?: number, label?: ComponentChildren }) {
	return <div class="md-editor">
		<label class="field">
			<span>{label ?? 'Text'}</span>
			<textarea rows={rows} value={value} onInput={event => onInput((event.currentTarget as HTMLTextAreaElement).value)} />
		</label>
		<div class="md-preview">
			<span class="muted small">Preview</span>
			{value.trim() ? <Markdown text={value} /> : <p class="muted">Nothing yet.</p>}
		</div>
		<p class="muted small md-help">
			<strong>**bold**</strong>, <em>*italic*</em>, # a heading, - a list, [a link](https://…), a table with | and ---.
			Pokémon names get their sprites, and a Pokepaste link shows its team.
		</p>
	</div>;
}
